/* Replay a map (POST /api/matches/:id/replay-map): pick a round of that map that has a backup, or
 * Round 0 (from 0-0). Explains what happens before confirming. Server errors (server busy, bracket
 * already moved on, …) are shown inline. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import McIcon from './mcIcons';

export default function ReplayMapModal({ matchId, mapIndex, mapName, team1Name, team2Name, laterMapsPlayed, isFinished, onClose, onStarted }) {
  const { t } = useTranslation();
  const [rounds, setRounds] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get(`/api/matches/${matchId}/backups`).then(res => {
      if (!alive) return;
      // only this map's rounds that have a backup file (round 0 is offered separately)
      const list = (res.data.rounds || [])
        .filter(r => Number(r.map_id) === Number(mapIndex) && r.filename && Number(r.round) > 0)
        .sort((a, b) => b.round - a.round);
      setRounds(list);
    }).catch(err => { if (alive) setLoadError(err.response?.data?.error || t('matchDetail.replayLoadError', 'Could not load this map\'s backups.')); });
    return () => { alive = false; };
  }, [matchId, mapIndex, t]);

  const submit = async () => {
    if (selected == null) return;
    setBusy(true); setError('');
    try {
      await axios.post(`/api/matches/${matchId}/replay-map`, { map_index: mapIndex, restore_round: selected });
      onStarted && onStarted(mapIndex, selected);
    } catch (err) {
      setError(err.response?.data?.error || t('matchDetail.replayFailed', 'Replay failed.'));
      setBusy(false);
    }
  };

  const option = (round, score1, score2, label) => (
    <button type="button" key={round} className={`rp-round${selected === round ? ' on' : ''}`} onClick={() => setSelected(round)}>
      <span className="rp-round-n">{label}</span>
      <span className="rp-round-score">
        <span className="rp-team">{team1Name}</span>
        <b>{score1}</b><i>-</i><b>{score2}</b>
        <span className="rp-team">{team2Name}</span>
      </span>
    </button>
  );

  return (
    <div className="rp-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="rp-modal" role="dialog" aria-modal="true" aria-label={t('matchDetail.replayTitle', 'Replay map {{n}}', { n: mapIndex + 1 })}>
        <div className="rp-head">
          <span className="rp-head-icon"><McIcon name="replay" size={16} /></span>
          <div>
            <div className="rp-title">{t('matchDetail.replayTitle', 'Replay map {{n}}', { n: mapIndex + 1 })}</div>
            <div className="rp-sub">{mapName}</div>
          </div>
          <button type="button" className="rp-x" onClick={onClose} disabled={busy} aria-label={t('common.close', 'Close')}>×</button>
        </div>

        <div className="rp-label">{t('matchDetail.replayPickRound', 'Restart from')}</div>
        <div className="rp-list">
          {rounds == null && !loadError && <div className="rp-empty">{t('matchDetail.replayLoading', 'Loading backups…')}</div>}
          {loadError && <div className="rp-empty err">{loadError}</div>}
          {rounds && rounds.map(r => option(r.round, r.score1 ?? '?', r.score2 ?? '?', t('matchDetail.replayRound', 'Round {{n}}', { n: r.round })))}
          {rounds && option(0, 0, 0, t('matchDetail.replayFromZero', 'Round 0 - from 0-0'))}
        </div>
        {rounds && rounds.length === 0 && <div className="rp-note">{t('matchDetail.replayNoBackups', 'No round backups for this map - it can only be replayed from 0-0.')}</div>}

        <div className="rp-warn">
          <div className="rp-warn-title">{t('matchDetail.replayWhatHappens', 'What happens')}</div>
          <ul>
            <li>{t('matchDetail.replayEffectMap', 'The server loads {{map}} again in warmup. When you start the match, the chosen round\'s backup is loaded (score and economy as they were).', { map: mapName })}</li>
            <li>{t('matchDetail.replayEffectRounds', 'Rounds, kills and stats after that point on this map are voided (kept for audit).')}</li>
            {laterMapsPlayed > 0 && <li>{t('matchDetail.replayEffectLater', 'Every later map ({{count}}) is voided and the series score is recalculated; those maps are played again after this one.', { count: laterMapsPlayed })}</li>}
            {isFinished && <li>{t('matchDetail.replayEffectFinished', 'This finished match is reopened: it goes back to live and its result is cleared (bracket too, if it hasn\'t moved on).')}</li>}
          </ul>
        </div>

        {error && <div className="rp-error">{error}</div>}

        <div className="rp-actions">
          <button type="button" className="mc-btn mc-neutral" onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
          <button type="button" className="mc-btn mc-solid mc-cyan" onClick={submit} disabled={busy || selected == null}>
            <McIcon name="replay" />{busy ? t('matchDetail.replayStarting', 'Starting…') : t('matchDetail.replayConfirm', 'Replay map {{n}}', { n: mapIndex + 1 })}
          </button>
        </div>
      </div>
    </div>
  );
}
